import { useState, useCallback, useMemo } from 'react';

// Define la forma de las reglas de validación
export interface ValidationRules<T> {
  [key: string]: (value: any, values: T) => string | null;
}

// Define lo que el Hook retorna
export interface FormHook<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubmit: (callback: () => void) => (e: React.FormEvent) => void;
  validate: (customValues?: T) => boolean;
  isValid: boolean;
  resetForm: () => void;
  setValues: React.Dispatch<React.SetStateAction<T>>;
  setErrors: React.Dispatch<React.SetStateAction<Partial<Record<keyof T, string>>>>;
}

const useForm = <T extends Record<string, any>>(
  initialValues: T,
  validationRules: ValidationRules<T>
): FormHook<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
  }, [initialValues]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validar campo al cambiar
    if (validationRules[name]) {
      const error = validationRules[name](value, { ...values, [name]: value });
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  }, [validationRules, values]);

  const validate = useCallback((customValues?: T): boolean => {
    let formErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;
    const targetValues = customValues || values;

    for (const key in validationRules) {
      const value = targetValues[key as keyof T];
      const error = validationRules[key](value, targetValues);
      if (error) {
        formErrors[key as keyof T] = error;
        isValid = false;
      }
    }

    setErrors(formErrors);
    return isValid;
  }, [validationRules, values]);

  const handleSubmit = useCallback((callback: () => void) => (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      callback();
    }
  }, [validate]);

  const isValid = useMemo(() => {
    return Object.keys(validationRules).every((key) => {
      return validationRules[key](values[key as keyof T], values) === null;
    });
  }, [validationRules, values]);

  return {
    values,
    errors,
    handleChange,
    handleSubmit,
    validate,
    isValid,
    resetForm,
    setValues,
    setErrors,
  };
};

export default useForm;
